import React, { useEffect, useMemo, useRef, useState } from "react";
import YouTube from "react-youtube";
import { useSearchParams } from "react-router-dom";
import {
  Youtube as YoutubeIcon,
  Flag,
  Square as StopIcon,
  Save,
  Trash2,
  Search,
  Play,
  Repeat,
  Tags as TagsIcon,
  X as XIcon,
  Download,
  Upload,
  ExternalLink,
  Crosshair,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  listTactics,
  listTacticTags,
  createTactic,
  deleteTactic,
  tacticsExportUrl,
  api as axiosApi,
  fehlerText,
} from "@/lib/api";
import TacticCalibrator from "@/components/TacticCalibrator";

// Curated tag suggestions — keep the chip row useful even when the library is empty.
const PRESET_TAGS = ["Offense", "Defense", "V-Stack", "Horizontal", "Iso", "Zone", "Man", "Huck", "Break", "End-Zone"];

/** Extract the 11-char video id from any YouTube URL or accept a bare id. */
function parseYoutubeId(input) {
  if (!input) return null;
  const s = input.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  const patterns = [
    /(?:youtube\.com\/watch\?[^#]*?v=)([A-Za-z0-9_-]{11})/,
    /(?:youtu\.be\/)([A-Za-z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = s.match(p);
    if (m) return m[1];
  }
  return null;
}

function fmt(s) {
  if (s == null) return "—";
  const total = Math.floor(s);
  const ms = Math.floor((s - total) * 100).toString().padStart(2, "0");
  const m = Math.floor(total / 60);
  const sec = (total % 60).toString().padStart(2, "0");
  return `${m}:${sec}.${ms}`;
}

export default function TacticCutter() {
  // ---------- Deep-link focus ----------
  // The Dashboard Flow-Map links here as `/admin/tactics?focus=<tactic_id>`.
  // When that param is present we scroll the matching row into view, auto-
  // open its calibrator panel, and pulse a brief highlight ring. Param is
  // consumed once and cleared so a manual refresh doesn't keep refocusing.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const [consumedFocus, setConsumedFocus] = useState(null);
  useEffect(() => {
    if (focusId && focusId !== consumedFocus) {
      setConsumedFocus(focusId);
      // Clear the param after a beat so re-navigations re-trigger focus.
      const timer = setTimeout(() => setSearchParams({}, { replace: true }), 1500);
      return () => clearTimeout(timer);
    }
  }, [focusId, consumedFocus, setSearchParams]);

  // ---------- Cutter state ----------
  const [urlInput, setUrlInput] = useState("");
  const [videoId, setVideoId] = useState(null);
  const playerRef = useRef(null);

  const [startS, setStartS] = useState(null);
  const [endS, setEndS] = useState(null);
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState([]);
  const [tagDraft, setTagDraft] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // ---------- Library state ----------
  const [items, setItems] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tagFilter, setTagFilter] = useState(null);
  const [search, setSearch] = useState("");

  const refresh = async () => {
    const [list, tagList] = await Promise.all([listTactics(), listTacticTags()]);
    setItems(list);
    setAllTags(tagList);
  };
  useEffect(() => { refresh(); }, []);

  // ---------- JSON import ----------
  const importFileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const handleImportFile = async (file) => {
    if (!file) return;
    setImporting(true);
    const tid = toast.loading(`Importing ${file.name}…`);
    try {
      const text = await file.text();
      let payload;
      try { payload = JSON.parse(text); }
      catch (e) {
        toast.error("File is not valid JSON", { id: tid });
        return;
      }
      const res = await axiosApi.post("/tactics/import", payload).then((r) => r.data);
      const errCount = (res.errors || []).length;
      const summary = `Imported ${res.inserted} · Skipped ${res.skipped} (dupes)` + (errCount ? ` · ${errCount} errors` : "");
      if (res.inserted > 0 || res.skipped > 0) {
        toast.success(summary, { id: tid });
      } else if (errCount > 0) {
        toast.error(summary + ` — first: ${res.errors[0]?.error}`, { id: tid });
      } else {
        toast.message(summary, { id: tid });
      }
      if (errCount > 0) {
        console.warn("[tactics import] errors", res.errors);
      }
      await refresh();
    } catch (err) {
      const msg = fehlerText(err) || err?.message || "Import failed";
      toast.error(typeof msg === "string" ? msg : "Import failed (validation)", { id: tid });
    } finally {
      setImporting(false);
      if (importFileRef.current) importFileRef.current.value = "";
    }
  };

  // ---------- YouTube player wiring ----------
  const loadUrl = () => {
    const id = parseYoutubeId(urlInput);
    if (!id) {
      toast.error("Couldn't parse a YouTube id from that input");
      return;
    }
    setVideoId(id);
    setStartS(null);
    setEndS(null);
  };

  const onPlayerReady = (e) => {
    playerRef.current = e.target;
  };

  const grabTime = () => {
    const p = playerRef.current;
    if (!p) return null;
    try { return Number(p.getCurrentTime() || 0); } catch (_) { return null; }
  };

  const markStart = () => {
    const t = grabTime();
    if (t == null) { toast.error("Player not ready"); return; }
    setStartS(t);
    toast.success(`Start: ${fmt(t)}`);
  };
  const markEnd = () => {
    const t = grabTime();
    if (t == null) { toast.error("Player not ready"); return; }
    setEndS(t);
    toast.success(`End: ${fmt(t)}`);
  };
  const previewSnippet = () => {
    const p = playerRef.current;
    if (!p || startS == null) return;
    p.seekTo(startS, true);
    p.playVideo();
  };

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t || tags.includes(t)) { setTagDraft(""); return; }
    setTags([...tags, t]);
    setTagDraft("");
  };
  const togglePresetTag = (t) => {
    setTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const canSave = videoId && title.trim() && startS != null && endS != null && endS > startS;

  const resetForm = () => {
    setStartS(null); setEndS(null); setTitle(""); setTags([]); setNotes(""); setTagDraft("");
  };

  const onSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const created = await createTactic({
        title: title.trim(),
        youtube_id: videoId,
        start_seconds: startS,
        end_seconds: endS,
        tags,
        notes: notes.trim() || null,
      });
      toast.success(`Saved: ${created.title} (${created.duration}s)`);
      resetForm();
      refresh();
    } catch (err) {
      const msg = fehlerText(err) || err?.message || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed (validation error)");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this tactic?")) return;
    setItems((prev) => prev.filter((t) => t.id !== id));
    try { await deleteTactic(id); toast.success("Deleted"); }
    catch (err) { toast.error(fehlerText(err) || "Delete failed"); }
    finally { refresh(); }
  };

  const filtered = useMemo(() => {
    let out = items;
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((t) => t.title.toLowerCase().includes(q));
    }
    if (tagFilter) out = out.filter((t) => (t.tags || []).includes(tagFilter));
    return out;
  }, [items, search, tagFilter]);

  return (
    <div className="min-h-screen" data-testid="tactic-cutter-page">
      <header className="px-8 pt-8 pb-6 border-b border-border">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          Library · YouTube
        </div>
        <h1 className="font-display font-black uppercase text-4xl tracking-tight flex items-center gap-3">
          <YoutubeIcon className="w-8 h-8 text-primary" /> Tactic Cutter
        </h1>
        <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Curate tactical snippets from public YouTube games · stored as id + timestamps only
        </div>
      </header>

      <div className="px-8 py-6 grid lg:grid-cols-5 gap-6">
        {/* ============ Cutter ============ */}
        <div className="lg:col-span-3 space-y-5">
          {/* URL bar */}
          <div className="border border-border bg-card rounded-sm p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
              Step 1 · Paste YouTube URL
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="tactic-url-input"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadUrl()}
                placeholder="https://www.youtube.com/watch?v=R6L_zY9L6w8 — or just R6L_zY9L6w8"
                className="bg-secondary border-border focus:border-primary font-mono text-xs"
              />
              <Button
                data-testid="tactic-load-btn"
                onClick={loadUrl}
                disabled={!urlInput}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest"
              >
                Load
              </Button>
            </div>
          </div>

          {/* Player */}
          {videoId && (
            <div className="border border-border bg-card rounded-sm overflow-hidden">
              <div className="aspect-video bg-black">
                <YouTube
                  videoId={videoId}
                  onReady={onPlayerReady}
                  opts={{
                    width: "100%",
                    height: "100%",
                    playerVars: { modestbranding: 1, rel: 0 },
                  }}
                  className="w-full h-full"
                  iframeClassName="w-full h-full"
                  data-testid="tactic-yt-player"
                />
              </div>

              {/* Cutter controls */}
              <div className="p-4 border-t border-border space-y-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Step 2 · Mark the tactical sequence
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <Button
                    data-testid="tactic-mark-start"
                    onClick={markStart}
                    className="bg-primary/15 text-primary border border-primary/40 hover:bg-primary hover:text-primary-foreground font-display font-bold uppercase tracking-widest text-xs"
                  >
                    <Flag className="w-4 h-4 mr-2" /> Set Start
                  </Button>
                  <Button
                    data-testid="tactic-mark-end"
                    onClick={markEnd}
                    className="bg-destructive/15 text-destructive border border-destructive/40 hover:bg-destructive hover:text-destructive-foreground font-display font-bold uppercase tracking-widest text-xs"
                  >
                    <StopIcon className="w-4 h-4 mr-2" /> Set End
                  </Button>
                  <Button
                    data-testid="tactic-preview"
                    variant="outline"
                    disabled={startS == null}
                    onClick={previewSnippet}
                    className="border-border bg-secondary hover:bg-accent text-foreground font-display font-bold uppercase tracking-widest text-xs disabled:opacity-40"
                  >
                    <Play className="w-4 h-4 mr-2" /> Preview from Start
                  </Button>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center pt-1">
                  <Stat label="Start" value={fmt(startS)} />
                  <Stat label="End" value={fmt(endS)} />
                  <Stat
                    label="Duration"
                    value={startS != null && endS != null && endS > startS ? `${(endS - startS).toFixed(1)}s` : "—"}
                    accent={endS != null && startS != null && endS <= startS ? "error" : null}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Metadata */}
          {videoId && (
            <div className="border border-border bg-card rounded-sm p-5 space-y-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Step 3 · Describe the sequence
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Title *</label>
                <Input
                  data-testid="tactic-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Perfekter Break-In · Inside-Out Inside-Endzone"
                  className="mt-1.5 bg-secondary border-border focus:border-primary"
                />
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <TagsIcon className="w-3 h-3" /> Tags
                </label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {PRESET_TAGS.map((t) => (
                    <button
                      key={t}
                      data-testid={`tactic-preset-tag-${t}`}
                      onClick={() => togglePresetTag(t)}
                      className={`px-2 py-1 rounded-sm border font-mono text-[10px] uppercase tracking-widest transition-colors ${
                        tags.includes(t)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-secondary text-muted-foreground border-border hover:text-foreground hover:border-primary/40"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <Input
                    data-testid="tactic-tag-input"
                    value={tagDraft}
                    onChange={(e) => setTagDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                    placeholder="Custom tag…"
                    className="bg-secondary border-border focus:border-primary font-mono text-xs"
                  />
                  <Button variant="outline" onClick={addTag} className="border-border bg-secondary hover:bg-accent font-mono text-xs uppercase tracking-widest">Add</Button>
                </div>
                {tags.filter((t) => !PRESET_TAGS.includes(t)).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {tags.filter((t) => !PRESET_TAGS.includes(t)).map((t) => (
                      <Badge key={t} className="bg-accent text-primary border border-primary/40 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1">
                        {t}
                        <button onClick={() => setTags(tags.filter((x) => x !== t))}>
                          <XIcon className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Notes (optional)</label>
                <Textarea
                  data-testid="tactic-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What makes this clip valuable? Phase, weather, opponent…"
                  className="mt-1.5 bg-secondary border-border focus:border-primary font-mono text-xs resize-none"
                  rows={3}
                />
              </div>

              <Button
                data-testid="tactic-save-btn"
                onClick={onSave}
                disabled={!canSave || saving}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest disabled:opacity-40"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving…" : "Save Tactic"}
              </Button>
            </div>
          )}
        </div>

        {/* ============ Library ============ */}
        <div className="lg:col-span-2 space-y-4">
          <div className="border border-border bg-card rounded-sm p-4">
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Tactic Library · {filtered.length}{filtered.length !== items.length && ` / ${items.length}`}
              </div>
              <div className="flex items-center gap-1.5">
                <input
                  ref={importFileRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  data-testid="tactic-import-input"
                  onChange={(e) => handleImportFile(e.target.files?.[0])}
                />
                <button
                  data-testid="tactic-import-btn"
                  type="button"
                  onClick={() => importFileRef.current?.click()}
                  disabled={importing}
                  title="Import a previously exported tactic-library.json"
                  className="px-2 py-1 bg-secondary border border-border hover:border-primary/40 hover:bg-accent text-foreground hover:text-primary rounded-sm font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  <Upload className="w-3 h-3" /> {importing ? "Importing…" : "Import JSON"}
                </button>
                <a
                  data-testid="tactic-export-json"
                  href={tacticsExportUrl()}
                  title="Download all tactics as JSON (training-data index)"
                  className="px-2 py-1 bg-secondary border border-border hover:border-primary/40 hover:bg-accent text-foreground hover:text-primary rounded-sm font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 transition-colors"
                >
                  <Download className="w-3 h-3" /> Export JSON
                </a>
              </div>
            </div>
            <div className="relative mb-3">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                data-testid="tactic-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title…"
                className="pl-8 bg-secondary border-border focus:border-primary font-mono text-xs"
              />
            </div>
            {allTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {allTags.map((t) => (
                  <button
                    key={t}
                    data-testid={`tactic-filter-${t}`}
                    onClick={() => setTagFilter(tagFilter === t ? null : t)}
                    className={`px-2 py-1 rounded-sm border font-mono text-[10px] uppercase tracking-widest transition-colors ${
                      tagFilter === t
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-secondary text-muted-foreground border-border hover:text-foreground hover:border-primary/40"
                    }`}
                  >
                    {t}
                  </button>
                ))}
                {tagFilter && (
                  <button
                    onClick={() => setTagFilter(null)}
                    className="px-2 py-1 rounded-sm border border-border bg-secondary hover:bg-accent font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
                  >
                    <XIcon className="w-3 h-3 inline" /> Clear
                  </button>
                )}
              </div>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-12 font-mono text-xs uppercase tracking-widest text-muted-foreground border border-dashed border-border rounded-sm">
              {items.length === 0 ? "No tactics yet — start by pasting a URL above." : "No tactics for those filters."}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((t) => (
                <TacticRow key={t.id} t={t} focus={focusId === t.id} onDelete={() => onDelete(t.id)} onTacticUpdated={refresh} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const accentClass = accent === "error" ? "text-destructive" : "text-foreground";
  return (
    <div className="border border-border bg-secondary rounded-sm py-2 px-3">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-mono text-sm mt-0.5 ${accentClass}`}>{value}</div>
    </div>
  );
}

function TacticRow({ t, focus, onDelete, onTacticUpdated }) {
  // null | "loop" | "calib" — only one panel open at a time
  const [mode, setMode] = useState(null);
  const rowRef = useRef(null);
  // When the Dashboard deep-links here with `?focus=ID`, scroll the row
  // into view, snap open the calibrator panel, and pulse a glow ring so
  // the admin can't miss where they landed.
  useEffect(() => {
    if (!focus) return;
    setMode("calib");
    const el = rowRef.current;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focus]);

  const startUrl = `https://www.youtube.com/watch?v=${t.youtube_id}&t=${Math.floor(t.start_seconds)}s`;
  const thumb = `https://img.youtube.com/vi/${t.youtube_id}/mqdefault.jpg`;
  const hasCalib = !!t.calibration;

  return (
    <div
      ref={rowRef}
      data-testid={`tactic-row-${t.id}`}
      data-focused={focus ? "true" : undefined}
      className={`border bg-card rounded-sm transition-all group ${
        focus
          ? "border-pink-400 ring-2 ring-pink-400/40 shadow-lg shadow-pink-400/20 animate-in zoom-in-95"
          : "border-border hover:border-primary/50"
      }`}
    >
      <div className="p-3 flex gap-3">
        <button
          type="button"
          onClick={() => setMode(mode === "loop" ? null : "loop")}
          className="flex-shrink-0 relative"
          title={mode === "loop" ? "Hide player" : "Open loop player"}
          data-testid={`tactic-thumb-${t.id}`}
        >
          <img src={thumb} alt="" className="w-28 h-16 object-cover rounded-sm" />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition-opacity">
            <Repeat className="w-5 h-5 text-white" />
          </div>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-medium text-sm truncate flex-1" title={t.title}>{t.title}</div>
            {hasCalib && (
              <Badge data-testid={`tactic-calib-badge-${t.id}`} className="bg-primary/15 text-primary border border-primary/40 font-mono text-[9px] uppercase tracking-widest flex items-center gap-1 flex-shrink-0">
                <Crosshair className="w-2.5 h-2.5" /> calibrated
              </Badge>
            )}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
            {fmt(t.start_seconds)} → {fmt(t.end_seconds)} · <span className="text-primary">{t.duration.toFixed(1)}s</span>
          </div>
          {(t.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {(t.tags || []).map((tag) => (
                <span key={tag} className="px-1.5 py-0.5 border border-border bg-secondary font-mono text-[9px] uppercase tracking-widest text-muted-foreground rounded-sm">{tag}</span>
              ))}
            </div>
          )}
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            <Button
              size="sm" variant="ghost"
              data-testid={`tactic-loop-toggle-${t.id}`}
              onClick={() => setMode(mode === "loop" ? null : "loop")}
              className={`h-6 px-2 font-mono text-[10px] uppercase tracking-widest ${mode === "loop" ? "bg-primary/15 text-primary" : "hover:bg-accent hover:text-primary"}`}
            >
              <Repeat className="w-3 h-3 mr-1" /> {mode === "loop" ? "Hide" : "Loop"}
            </Button>
            <Button
              size="sm" variant="ghost"
              data-testid={`tactic-calib-toggle-${t.id}`}
              onClick={() => setMode(mode === "calib" ? null : "calib")}
              className={`h-6 px-2 font-mono text-[10px] uppercase tracking-widest ${mode === "calib" ? "bg-primary/15 text-primary" : "hover:bg-accent hover:text-primary"}`}
            >
              <Crosshair className="w-3 h-3 mr-1" /> Calibrate
            </Button>
            <a
              href={startUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="h-6 px-2 inline-flex items-center hover:bg-accent hover:text-primary text-muted-foreground rounded-sm font-mono text-[10px] uppercase tracking-widest gap-1"
              title="Open on YouTube at start time"
            >
              <ExternalLink className="w-3 h-3" /> YT
            </a>
          </div>
        </div>
        <Button
          data-testid={`tactic-delete-${t.id}`}
          size="sm" variant="ghost"
          onClick={onDelete}
          className="h-7 w-7 p-0 hover:bg-destructive/20 hover:text-destructive self-start"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
      {mode === "loop" && <LoopPlayer tactic={t} />}
      {mode === "calib" && <TacticCalibrator tactic={t} onSaved={onTacticUpdated} />}
    </div>
  );
}

/**
 * Inline YouTube player that loops the marked snippet endlessly.
 *
 * It polls `getCurrentTime()` every 250 ms; when the playhead crosses
 * `end_seconds`, it seeks back to `start_seconds`. Perfect for studying a
 * specific motion pattern without scrubbing manually.
 */
function LoopPlayer({ tactic }) {
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const [paused, setPaused] = useState(false);

  const onReady = (e) => {
    playerRef.current = e.target;
    try {
      e.target.seekTo(tactic.start_seconds, true);
      e.target.playVideo();
    } catch (_) { /* iframe not ready yet */ }
  };

  // Loop watchdog
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const p = playerRef.current;
      if (!p || paused) return;
      try {
        const t = p.getCurrentTime();
        if (t >= tactic.end_seconds - 0.05) {
          p.seekTo(tactic.start_seconds, true);
          p.playVideo();
        }
      } catch (_) { /* player not ready */ }
    }, 250);
    return () => clearInterval(intervalRef.current);
  }, [tactic.start_seconds, tactic.end_seconds, paused]);

  return (
    <div className="border-t border-border bg-black/40" data-testid={`tactic-loop-player-${tactic.id}`}>
      <div className="aspect-video bg-black">
        <YouTube
          videoId={tactic.youtube_id}
          onReady={onReady}
          onStateChange={(e) => {
            // YT.PlayerState.PAUSED = 2
            setPaused(e.data === 2);
          }}
          opts={{
            width: "100%",
            height: "100%",
            playerVars: {
              modestbranding: 1,
              rel: 0,
              start: Math.floor(tactic.start_seconds),
              autoplay: 1,
              controls: 1,
            },
          }}
          className="w-full h-full"
          iframeClassName="w-full h-full"
        />
      </div>
      <div className="px-3 py-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-t border-border">
        <span className="flex items-center gap-1.5">
          <Repeat className="w-3 h-3 text-primary" /> Looping {fmt(tactic.start_seconds)} → {fmt(tactic.end_seconds)}
        </span>
        <span className="text-primary">{tactic.duration.toFixed(1)}s · pause player to interrupt</span>
      </div>
    </div>
  );
}
